import React, { useState, useEffect } from "react";
import { Tabs, Tab, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlus, faTrash, faArrowUp, faArrowDown, faUpload
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-responsive-modal";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import ChatGptAssistModal from "components/ChatGptAssistModal";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import { useDropzone } from 'react-dropzone';

const VideoSettings = ({ lesson, onChange }) => {
  const [videoSource, setVideoSource] = useState(lesson.videoSource || 'external');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  
  // Check if content is a URL or File
  useEffect(() => {
    if (lesson.content instanceof File) {
      // Handle direct file upload
      setVideoSource('upload');
      setVideoFile(lesson.content);
      setVideoPreview(URL.createObjectURL(lesson.content));
    } else if (typeof lesson.content === 'object' && lesson.content?.url) {
      // Handle server-stored video (S3 bucket URL in an object)
      setVideoSource('upload');
      setVideoPreview(lesson.content.url);
    } else if (typeof lesson.content === 'string' && lesson.content.trim() !== '') {
      setVideoSource('external');
    }
  }, [lesson.content]);
  
  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setVideoFile(file);
      onChange('content', file);
      
      const previewUrl = URL.createObjectURL(file);
      setVideoPreview(previewUrl);
    }
  };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      'video/quicktime': ['.mov']
    },
    maxFiles: 1,
    multiple: false
  });
  
  const handleVideoSourceChange = (e) => {
    const source = e.target.value;
    setVideoSource(source);
    
    if (source === 'upload') {
      if (!videoFile && !videoPreview) {
        onChange('content', '');
      }
    } else {
      setVideoFile(null);
      setVideoPreview(null);
      onChange('content', '');
    }
  };
  
  const handleVideoUrlChange = (e) => {
    const url = e.target.value;
    
    const convertedUrl = convertToEmbedUrl(url);
    onChange('content', convertedUrl);
  };
  
  // Function to convert various video platform URLs to embeddable format
  const convertToEmbedUrl = (url) => {
    if (!url) return url;
    
    const youtubeRegex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    const vimeoRegex = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|)(\d+)(?:|\/\?)/;
    const vimeoMatch = url.match(vimeoRegex);
    
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  };
  
  return (
    <div className="video-settings-container p-3 mb-4 bg-light rounded border">
      <h5 className="mb-3">Video Settings</h5>
      
      <Form.Group className="mb-3">
        <Form.Label>Video Source</Form.Label>
        <Form.Select 
          value={videoSource} 
          onChange={handleVideoSourceChange}
        >
          <option value="upload">Upload from library</option>
          <option value="external">External Video</option>
        </Form.Select>
      </Form.Group>
      
      {videoSource === 'upload' ? (
        <div className="mb-3">
          <Form.Label>Upload Video</Form.Label>
          <div 
            {...getRootProps()} 
            className={`dropzone p-4 rounded border text-center ${isDragActive ? 'bg-light border-primary' : ''}`}
            style={{ cursor: 'pointer' }}
          >
            <input {...getInputProps()} />
            <FontAwesomeIcon icon={faUpload} className="mb-2" style={{ fontSize: '2rem' }} />
            <p>Drag & drop a video file here, or click to select</p>
            <p className="text-muted small">Supports: MP4, WebM, MOV</p>
          </div>
          
          {/* Video preview */}
          {videoPreview && (
            <div className="mt-3">
              <h6>Video Preview</h6>
              <div className="video-preview-container">
                <video 
                  controls 
                  className="w-100" 
                  src={videoPreview}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}
        </div>
      ) : (
        <Form.Group className="mb-3">
          <Form.Label>Video URL</Form.Label>
          <Form.Control
            type="text"
            value={typeof lesson.content === 'string' ? lesson.content : ''}
            onChange={handleVideoUrlChange}
            placeholder="Enter YouTube, Vimeo video URL"
          />
          <Form.Text className="text-muted">
            Enter the embed URL for YouTube, Vimeo
          </Form.Text>
        </Form.Group>
      )}
    </div>
  );
};

const CourseModuleEditor = ({ module, onChange }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [lessonToDeleteIdx, setLessonToDeleteIdx] = useState(null);
  const [showCtaFields, setShowCtaFields] = useState({});
  
  // Reset to basic tab whenever the module changes
  useEffect(() => {
    setActiveTab("basic");
  }, [module.id]);
  
  useEffect(() => {
    if (module.lessons && module.lessons.length > 0) {
      const ctaVisibility = {};
      module.lessons.forEach((lesson, idx) => {
        ctaVisibility[idx] = !!lesson.cta;
      });
      setShowCtaFields(ctaVisibility);
    }
  }, [module.lessons]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    onChange({
      ...module,
      [name]: type === "checkbox" ? checked : value
    });
  };
  
  // const handleTypeChange = (e) => {
  //   const newType = e.target.value;
  //   let updatedModule = {
  //     ...module,
  //     type: newType
  //   };
    
  //   // Initialize appropriate fields based on module type
  //   if (newType === "assessment" && !module.questions) {
  //     updatedModule.questions = [];
  //   }
    
  //   onChange(updatedModule);
  // };
  
  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      type: "multiple-choice",
      text: "",
      options: ["Option 1", "Option 2"],
      isRequired: true
    };
    
    onChange({
      ...module,
      questions: [...(module.questions || []), newQuestion]
    });
  };
  
  const updateQuestion = (index, updatedQuestion) => {
    const updatedQuestions = [...module.questions];
    updatedQuestions[index] = updatedQuestion;
    
    onChange({
      ...module,
      questions: updatedQuestions
    });
  };
  
  const removeQuestion = (index) => {
    const updatedQuestions = module.questions.filter((_, i) => i !== index);
    
    onChange({
      ...module,
      questions: updatedQuestions
    });
  };
  
  const addOptionToQuestion = (questionIndex) => {
    const updatedQuestions = [...module.questions];
    updatedQuestions[questionIndex].options.push(`Option ${updatedQuestions[questionIndex].options.length + 1}`);
    
    onChange({
      ...module,
      questions: updatedQuestions
    });
  };
  
  const updateQuestionOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...module.questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    
    onChange({
      ...module,
      questions: updatedQuestions
    });
  };
  
  const removeQuestionOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...module.questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    
    onChange({
      ...module,
      questions: updatedQuestions
    });
  };

  const setDescriptionValue = (value) => {
    onChange({
      ...module,
      description: value
    });
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'blockquote', 'code-block'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'blockquote', 'code-block',
    'color', 'background',
  ];

  const lessonTypes = [
    { value: "text", label: "Text" },
    { value: "video", label: "Video" },
    { value: "quiz", label: "Quiz" },
    { value: "assignment", label: "Assignment" },
  ];

  const defaultLesson = () => ({
    id: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    title: "",
    type: "text",
    duration: 10,
    content: "",
    cta: "",
    attachments: [],
    review: true,
  });

  const handleLessonChange = (lessonIdx, field, value) => {
    const updatedLessons = [...(module.lessons || [])];
    if (!updatedLessons[lessonIdx]) {
      updatedLessons[lessonIdx] = defaultLesson();
    }
    
    if (field === "duration") {
      const valueStr = String(value);
      
      if (valueStr.startsWith('0') && valueStr.length > 1) {
        value = parseInt(valueStr, 10);
      }
    }
    
    if (updatedLessons[lessonIdx][field] !== value) {
      updatedLessons[lessonIdx] = {
        ...updatedLessons[lessonIdx],
        [field]: value
      };
      
      onChange({
        ...module,
        lessons: updatedLessons
      });
    }
  };

  const handleLessonContentChange = (lessonIdx, value) => {
    const updatedLessons = [...(module.lessons || [])];
    if (!updatedLessons[lessonIdx]) {
      updatedLessons[lessonIdx] = defaultLesson();
    }
    
    if (updatedLessons[lessonIdx].content !== value) {
      updatedLessons[lessonIdx] = {
        ...updatedLessons[lessonIdx],
        content: value
      };
      
      onChange({
        ...module,
        lessons: updatedLessons
      });
    }
  };

  const handleLessonCtaChange = (lessonIdx, value) => {
    const updatedLessons = [...(module.lessons || [])];
    if (!updatedLessons[lessonIdx]) {
      updatedLessons[lessonIdx] = defaultLesson();
    }
    
    if (updatedLessons[lessonIdx].cta !== value) {
      updatedLessons[lessonIdx] = {
        ...updatedLessons[lessonIdx],
        cta: value
      };
      
      onChange({
        ...module,
        lessons: updatedLessons
      });
    }
  };

  const toggleCtaField = (lessonIdx) => {
    setShowCtaFields(prev => ({
      ...prev,
      [lessonIdx]: !prev[lessonIdx]
    }));
    
    if (showCtaFields[lessonIdx]) {
      handleLessonCtaChange(lessonIdx, "");
    }
  };

  const handleLessonAttachment = (lessonIdx, file) => {
    const updatedLessons = module.lessons ? [...module.lessons] : [];
    if (!updatedLessons[lessonIdx].attachments) {
      updatedLessons[lessonIdx].attachments = [];
    }
    updatedLessons[lessonIdx].attachments = [...updatedLessons[lessonIdx].attachments, file];
    onChange({ ...module, lessons: updatedLessons });
  };

  const handleRemoveLessonAttachment = (lessonIdx, fileIdx) => {
    const updatedLessons = module.lessons ? [...module.lessons] : [];
    updatedLessons[lessonIdx].attachments = updatedLessons[lessonIdx].attachments.filter((_, i) => i !== fileIdx);
    onChange({ ...module, lessons: updatedLessons });
  };

  // Helper function to create a proper attachment object for the AttachmentPreview component
  const prepareAttachmentForPreview = (attachment) => {
    if (attachment instanceof File) {
      return attachment;
    }
    
    if (attachment.url) {
      return {
        name: attachment.originalName || attachment.fileName || "Attachment",
        type: attachment.mimeType || attachment.type || "application/octet-stream",
        url: attachment.url,
        preview: attachment.url,
        size: attachment.size || 0,
        mimeType: attachment.mimeType || attachment.type,
        fileName: attachment.fileName || attachment.name || "attachment",
        originalName: attachment.originalName || attachment.name || "Attachment"
      };
    }
    
    return attachment;
  };

  const handleAddLesson = () => {
    const updatedLessons = module.lessons ? [...module.lessons] : [];
    const newLesson = defaultLesson();
    updatedLessons.push(newLesson);
    onChange({ ...module, lessons: updatedLessons });
    
    setShowCtaFields(prev => ({
      ...prev,
      [updatedLessons.length - 1]: false
    }));
    
    setTimeout(() => {
      const newLessonElement = document.getElementById(newLesson.id);
      if (newLessonElement) {
        newLessonElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleRemoveLesson = (lessonIdx) => {
    setLessonToDeleteIdx(lessonIdx);
    setShowConfirmModal(true);
  };
  
  const confirmRemoveLesson = () => {
    const updatedLessons = module.lessons ? [...module.lessons] : [];
    updatedLessons.splice(lessonToDeleteIdx, 1);
    onChange({ ...module, lessons: updatedLessons });
    setShowConfirmModal(false);
    setLessonToDeleteIdx(null);
  };

  const handleMoveLesson = (lessonIdx, direction) => {
    const updatedLessons = module.lessons ? [...module.lessons] : [];
    if (
      (direction === "up" && lessonIdx === 0) ||
      (direction === "down" && lessonIdx === updatedLessons.length - 1)
    ) {
      return;
    }
    const targetIdx = direction === "up" ? lessonIdx - 1 : lessonIdx + 1;
    const temp = updatedLessons[lessonIdx];
    updatedLessons[lessonIdx] = updatedLessons[targetIdx];
    updatedLessons[targetIdx] = temp;
    onChange({ ...module, lessons: updatedLessons });
  };
  
  return (
    <div className="course-module-editor">
      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key)}
        className="mb-4"
      >
        <Tab eventKey="basic" title="Basic Information">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Module Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={module.title || ""}
                onChange={handleInputChange}
                placeholder="Enter module title"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <div className="position-relative">
                <ChatGptAssistModal setValue={setDescriptionValue} />
                <Form.Control
                  as="textarea"
                  rows={6}
                  name="description"
                  value={module.description || ""}
                  onChange={handleInputChange}
                  placeholder="Brief description of this module"
                />
              </div>
            </Form.Group>
          </Form>
        </Tab>
        
        <Tab eventKey="content" title="Content">
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Lessons</h5>
              <Button variant="primary" size="sm" onClick={handleAddLesson}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Lesson
              </Button>
            </div>
            {(!module.lessons || module.lessons.length === 0) && (
              <div className="text-muted text-center py-3">No lessons added yet</div>
            )}
            {module.lessons && module.lessons.map((lesson, idx) => (
              <div key={lesson.id} id={lesson.id} className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <span className="fw-bold">Lesson {idx + 1}:</span> {lesson.title || <span className="text-muted">Untitled</span>}
                  </div>
                  <div>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-secondary p-1"
                      onClick={() => handleMoveLesson(idx, "up")}
                      disabled={idx === 0}
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-secondary p-1"
                      onClick={() => handleMoveLesson(idx, "down")}
                      disabled={idx === module.lessons.length - 1}
                    >
                      <FontAwesomeIcon icon={faArrowDown} />
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-1"
                      onClick={() => handleRemoveLesson(idx)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </div>
                <div className="card-body">
                  <Form.Group className="mb-3">
                    <Form.Label>Lesson Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={lesson.title}
                      onChange={e => handleLessonChange(idx, "title", e.target.value)}
                      placeholder="Enter lesson title"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Lesson Type</Form.Label>
                    <Form.Select
                      value={lesson.type}
                      onChange={e => handleLessonChange(idx, "type", e.target.value)}
                    >
                      {lessonTypes.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Lesson Duration (In Minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      value={lesson.duration === undefined || lesson.duration === null || lesson.duration === '' ? '' : Number(lesson.duration)}
                      min="0"
                      max="999"
                      onChange={e => {
                        const inputValue = e.target.value;
                        
                        if (inputValue.startsWith('0') && inputValue.length > 1) {
                          const cleanValue = parseInt(inputValue, 10);
                          e.target.value = cleanValue.toString();
                          handleLessonChange(idx, "duration", cleanValue);
                        } else if (inputValue === '') {
                          handleLessonChange(idx, "duration", '');
                        } else {
                          const numValue = Number(inputValue);
                          handleLessonChange(idx, "duration", numValue);
                        }
                      }}
                      onBlur={e => {
                        if (e.target.value === '' || Number(e.target.value) < 1) {
                          handleLessonChange(idx, "duration", 1);
                        }
                      }}
                    />
                    <Form.Text className={Number(lesson.duration) > 999 ? "text-danger" : "text-muted"}>
                      {Number(lesson.duration) > 999 
                        ? "Warning: Duration exceeds recommended maximum of 999 minutes" 
                        : "Enter a value between 1 and 999 minutes"}
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Allow review for this lesson"
                      checked={lesson.review === undefined ? true : lesson.review}
                      onChange={e => handleLessonChange(idx, "review", e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Lesson Content</Form.Label>
                    <div className="position-relative">
                      {lesson.type !== 'video' && (
                        <div className="gpt-div">
                          <ChatGptAssistModal setValue={val => handleLessonContentChange(idx, val)} />
                        </div>
                      )}
                      {lesson.type === 'video' ? (
                        <VideoSettings 
                          lesson={lesson} 
                          onChange={(field, value) => handleLessonChange(idx, field, value)} 
                        />
                      ) : (
                        <ReactQuill
                          theme="snow"
                          value={lesson.content}
                          onChange={val => handleLessonContentChange(idx, val)}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Enter the content for this lesson..."
                        />
                      )}
                    </div>
                  </Form.Group>
                  
                  {/* Only show attachments section for non-video lessons */}
                  {lesson.type !== 'video' && (
                    <div className="mb-3">
                      <p><strong>Attachments (optional)</strong></p>
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <AttachmentSelection 
                          onChange={file => handleLessonAttachment(idx, file)} 
                          allowDocuments={true}
                          accepted={['.png', '.jpg', '.jpeg', '.mp4', '.mp3', '.webm', '.pdf', '.doc', '.docx', '.ppt', '.pptx', '.txt']}
                        />
                        {lesson.attachments && lesson.attachments.map((file, fileIdx) => (
                          <AttachmentPreview
                            key={fileIdx}
                            commentAttachmentPreview={"moduleAttachmentPreview"}
                            attachment={prepareAttachmentForPreview(file)}
                            clearAttachment={() => handleRemoveLessonAttachment(idx, fileIdx)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!showCtaFields[idx] ? (
                    <div className="mb-3">
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={() => toggleCtaField(idx)}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Add Call to Action
                      </Button>
                    </div>
                  ) : (
                    <Form.Group className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label>Lesson CTA (Call to Action)</Form.Label>
                        <Button 
                          variant="link" 
                          className="text-danger p-0" 
                          onClick={() => toggleCtaField(idx)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                      <div className="position-relative">
                        <div className="gpt-div">
                          <ChatGptAssistModal setValue={val => handleLessonCtaChange(idx, val)} />
                        </div>
                        <ReactQuill
                          theme="snow"
                          value={lesson.cta || ""}
                          onChange={val => handleLessonCtaChange(idx, val)}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Enter a call to action for this lesson..."
                        />
                      </div>
                    </Form.Group>
                  )}
                  
                </div>
              </div>
            ))}
          </div>
        </Tab>
        
        {(module.type === "assessment" || module.type === "quiz") && (
          <Tab eventKey="questions" title="Questions">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h5>Assessment Questions</h5>
              <Button variant="primary" size="sm" onClick={addQuestion}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Question
              </Button>
            </div>
            
            {module.questions?.map((question, qIndex) => (
              <div key={question.id} className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Question {qIndex + 1}</h6>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="text-danger p-0"
                    onClick={() => removeQuestion(qIndex)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
                
                <div className="card-body">
                  <Form.Group className="mb-3">
                    <Form.Label>Question Type</Form.Label>
                    <Form.Select
                      value={question.type}
                      onChange={(e) => updateQuestion(qIndex, { ...question, type: e.target.value })}
                    >
                      <option value="multiple-choice">Multiple Choice</option>
                      <option value="text">Free Text Response</option>
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Question Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={question.text}
                      onChange={(e) => updateQuestion(qIndex, { ...question, text: e.target.value })}
                      placeholder="Enter your question here"
                    />
                  </Form.Group>
                  
                  {question.type === "multiple-choice" && (
                    <div className="mb-3">
                      <Form.Label>Options</Form.Label>
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="input-group mb-2">
                          <Form.Control
                            type="text"
                            value={option}
                            onChange={(e) => updateQuestionOption(qIndex, oIndex, e.target.value)}
                            placeholder={`Option ${oIndex + 1}`}
                          />
                          <Button
                            variant="outline-danger"
                            onClick={() => removeQuestionOption(qIndex, oIndex)}
                            disabled={question.options.length <= 2}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => addOptionToQuestion(qIndex)}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Add Option
                      </Button>
                    </div>
                  )}
                  
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="This question is required"
                      checked={question.isRequired}
                      onChange={(e) => updateQuestion(qIndex, { ...question, isRequired: e.target.checked })}
                    />
                  </Form.Group>
                </div>
              </div>
            ))}
          </Tab>
        )}
      </Tabs>
      
      {/* Confirmation Modal for Lesson Deletion */}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        showCloseIcon={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        classNames={{
          modalContainer: "modalContainer",
          modal: "createCourseModal",
        }}
      >
        <div className="modal fade popup-modal create-task-popup">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-center createTaskHeading">Delete Lesson</h5>
            <CloseIcon style={{ width: "20px", cursor: "pointer" }} onClick={() => setShowConfirmModal(false)} />
          </div>
          <div className="mb-4">
            <p>Are you sure you want to delete this lesson? This action cannot be undone.</p>
          </div>
          <div className="d-flex justify-content-end">
            <Button variant="secondary" className="me-2" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmRemoveLesson}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseModuleEditor;